export * from "./_mutation";

import { gql, useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { AUTH, CLIENT_AUTH_REQUEST_TYPE, JWT } from "~/apollo/_config";
import {
  GET_COMPANY_EMAIL_THREAD_QUERY,
  GET_EMAIL_THREAD_QUERY,
  LIST_EMAIL_FOLDERS_QUERY,
} from "./_mutation";

const GET_EMAIL_AUTH_LINK_QUERY = gql`
  query getEmailAuthUrl($scope: String!) {
    getEmailAuthUrl(scope: $scope)
  }
`;

const LIST_EMAIL_CHILD_FOLDERS_QUERY = gql`
  query listEmailChildFolders(
    $scope: String!
    $folderId: String!
    $pageToken: String
    $emailAddressId: ID!
  ) {
    listEmailChildFolders(
      scope: $scope
      folderId: $folderId
      pageToken: $pageToken
      emailAddressId: $emailAddressId
    ) {
      pageToken
      folders {
        id
        displayName
        attributes
        parentFolderId
        childFolderCount
        unreadItemCount
        totalItemCount
        isHidden
      }
    }
  }
`;

const LIST_EMAIL_CONTACTS_QUERY = gql`
  query listEmailContacts(
    $scope: String!
    $pageToken: String
    $search: String
    $emailAddressId: ID!
  ) {
    listEmailContacts(
      scope: $scope
      pageToken: $pageToken
      search: $search
      emailAddressId: $emailAddressId
    ) {
      pageToken
      contacts
    }
  }
`;

const LIST_EMAIL_THREADS_BY_FOLDER_QUERY = gql`
  query listEmailThreadsByFolder(
    $scope: String!
    $pageToken: String
    $folderId: String!
    $search: String
    $emailAddressId: ID!
  ) {
    listEmailThreadsByFolder(
      scope: $scope
      pageToken: $pageToken
      folderId: $folderId
      search: $search
      emailAddressId: $emailAddressId
    ) {
      pageToken
      threads {
        id
        hasAttachments
        latestMessageReceivedDate
        latestMessageSentDate
        earliestMessageDate
        messageIds
        participants {
          email
          name
        }
        snippet
        subject
        unread
        linkedTickets {
          ticketId
          title
          _id
        }
        linkedSupportTickets {
          _id
          supportTicketId
          title
        }
      }
    }
  }
`;

const GET_EMAIL_MESSAGE_QUERY = gql`
  query getEmailMessage(
    $scope: String!
    $messageId: String!
    $emailAddressId: ID!
  ) {
    getEmailMessage(
      scope: $scope
      messageId: $messageId
      emailAddressId: $emailAddressId
    ) {
      id
      createdDateTime
      receivedDateTime
      sentDateTime
      hasAttachments
      subject
      bodyPreview
      parentFolderId
      conversationId
      isRead
      isDraft
      body {
        contentType
        content
      }
      from {
        name
        email
      }
      toRecipients {
        name
        email
      }
      ccRecipients {
        name
        email
      }
      bccRecipients {
        name
        email
      }
      replyTo {
        name
        email
      }
    }
  }
`;

const GET_EMAIL_MESSAGE_ATTACHMENTS_QUERY = gql`
  query getEmailMessageAttachments(
    $scope: String!
    $messageId: String!
    $emailAddressId: ID!
  ) {
    getEmailMessageAttachments(
      scope: $scope
      messageId: $messageId
      emailAddressId: $emailAddressId
    ) {
      id
      name
      contentType
      size
      isInline
      contentId
      lastModifiedDateTime
    }
  }
`;

const LIST_EMAIL_THREADS_BY_TICKET_QUERY = gql`
  query listEmailThreadsByTicket(
    $scope: String!
    $ticketId: ID!
    $emailAddressId: ID!
  ) {
    listEmailThreadsByTicket(
      scope: $scope
      ticketId: $ticketId
      emailAddressId: $emailAddressId
    ) {
      id
      subject
      hasAttachments
      snippet
      unread
      messageIds
      date
      earliestMessageDate
      latestMessageSentDate
      latestMessageReceivedDate
      accountId
      accountEmail
      canReply
      participants {
        name
        email
      }
      linkedTickets {
        _id
        ticketId
        title
      }
      linkedSupportTickets {
        _id
        supportTicketId
        title
      }
    }
  }
`;

const LIST_EMAIL_THREADS_BY_SUPPORT_TICKET_QUERY = gql`
  query listEmailThreadsBySupportTicket(
    $scope: String!
    $supportTicketId: ID!
    $emailAddressId: ID!
  ) {
    listEmailThreadsBySupportTicket(
      scope: $scope
      supportTicketId: $supportTicketId
      emailAddressId: $emailAddressId
    ) {
      id
      subject
      hasAttachments
      snippet
      unread
      messageIds
      date
      earliestMessageDate
      latestMessageSentDate
      latestMessageReceivedDate
      participants {
        name
        email
      }
      linkedSupportTickets {
        _id
        supportTicketId
        title
      }
    }
  }
`;

// Emails involving a company's contacts, searched live in the OEM mailboxes.
// Summaries only (no messages); a null emailAddressId means every connected
// OEM mailbox ("All"), so each thread carries its own accountId.
const LIST_EMAIL_THREADS_BY_COMPANY_QUERY = gql`
  query listEmailThreadsByCompany(
    $companyId: ID!
    $emailAddressId: ID
    $pageToken: String
  ) {
    listEmailThreadsByCompany(
      companyId: $companyId
      emailAddressId: $emailAddressId
      pageToken: $pageToken
    ) {
      threads {
        id
        subject
        hasAttachments
        snippet
        unread
        messageIds
        date
        earliestMessageDate
        latestMessageSentDate
        latestMessageReceivedDate
        accountId
        accountEmail
        accountType
        canReply
        participants {
          name
          email
        }
      }
      pageToken
    }
  }
`;

// Mailboxes for the company Email tab's picker: the OEM's connected accounts,
// then personal mailboxes with a linked thread the caller may see here.
const LIST_COMPANY_EMAIL_ACCOUNTS_QUERY = gql`
  query listCompanyEmailAccounts($companyId: ID!) {
    listCompanyEmailAccounts(companyId: $companyId) {
      _id
      emailAddress
      type
      ownerName
      emailAccountStatus
    }
  }
`;

const SAVE_EMAIL_DRAFT_MUTATION = gql`
  mutation saveEmailDraft($scope: String!, $input: InputSaveDraft!) {
    saveEmailDraft(scope: $scope, input: $input)
  }
`;

const DELETE_EMAIL_DRAFT_MUTATION = gql`
  mutation deleteEmailDraft(
    $scope: String!
    $draftId: String!
    $emailAddressId: ID!
  ) {
    deleteEmailDraft(
      scope: $scope
      draftId: $draftId
      emailAddressId: $emailAddressId
    )
  }
`;

const PERMANENTLY_DELETE_EMAILS_MUTATION = gql`
  mutation permanentlyDeleteEmails(
    $scope: String!
    $messageIds: [String!]!
    $emailAddressId: ID!
  ) {
    permanentlyDeleteEmails(
      scope: $scope
      messageIds: $messageIds
      emailAddressId: $emailAddressId
    )
  }
`;

const MARK_EMAIL_READ_UNREAD_MUTATION = gql`
  mutation markEmailReadUnread(
    $scope: String!
    $conversationId: String!
    $unread: Boolean!
    $emailAddressId: ID!
  ) {
    markEmailReadUnread(
      scope: $scope
      conversationId: $conversationId
      unread: $unread
      emailAddressId: $emailAddressId
    )
  }
`;

const UPDATE_EMAIL_CONVERSATION_MUTATION = gql`
  mutation updateEmailConversation(
    $scope: String!
    $input: InputUpdateEmailConversation!
  ) {
    updateEmailConversation(scope: $scope, input: $input) {
      ticket {
        _id
        ticketId
        title
      }
      supportTicket {
        _id
        supportTicketId
        title
      }
    }
  }
`;

const LIST_EMAIL_FOLDERS = "listEmailFolders";
const LIST_EMAIL_CHILD_FOLDERS = "listEmailChildFolders";
const LIST_EMAIL_CONTACTS = "listEmailContacts";
const LIST_EMAIL_THREADS_BY_FOLDER = "listEmailThreadsByFolder";

export const useListFolders = ({
  emailAddressId = "",
  skipCondition = true,
  scope = "oem",
} = {}) => {
  const { data, loading, fetchMore, refetch } = useQuery(
    LIST_EMAIL_FOLDERS_QUERY,
    {
      variables: { scope, pageToken: "", emailAddressId },
      fetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange: true,
      skip: skipCondition,
    },
  );

  const result = data?.[LIST_EMAIL_FOLDERS];

  const handleFetchMore = () => {
    if (!result?.pageToken) return;

    fetchMore({
      variables: { scope, pageToken: result.pageToken, emailAddressId },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;

        const prevData = prev[LIST_EMAIL_FOLDERS];
        const newData = fetchMoreResult[LIST_EMAIL_FOLDERS];

        return {
          [LIST_EMAIL_FOLDERS]: {
            ...newData,
            folders: [
              ...(prevData?.folders || []),
              ...(newData?.folders || []),
            ],
          },
        };
      },
    });
  };

  return {
    folders: result?.folders || [],
    hasMore: !!result?.pageToken,
    loading,
    fetchMore: handleFetchMore,
    refetch,
  };
};

export const useListChildFolders = ({
  emailAddressId,
  folderId,
  skip: skipCondition = false,
  scope = "oem",
}) => {
  const { data, loading, fetchMore } = useQuery(
    LIST_EMAIL_CHILD_FOLDERS_QUERY,
    {
      variables: { scope, pageToken: "", emailAddressId, folderId },
      fetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange: true,
      skip: skipCondition,
    },
  );

  const result = data?.[LIST_EMAIL_CHILD_FOLDERS];

  const handleFetchMore = () => {
    if (!result?.pageToken) return;

    fetchMore({
      variables: {
        scope,
        pageToken: result.pageToken,
        emailAddressId,
        folderId,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;

        const prevData = prev[LIST_EMAIL_CHILD_FOLDERS];
        const newData = fetchMoreResult[LIST_EMAIL_CHILD_FOLDERS];

        return {
          [LIST_EMAIL_CHILD_FOLDERS]: {
            ...newData,
            folders: [
              ...(prevData?.folders || []),
              ...(newData?.folders || []),
            ],
          },
        };
      },
    });
  };

  return {
    folders: result?.folders || [],
    hasMore: !!result?.pageToken,
    loading,
    fetchMore: handleFetchMore,
  };
};

export const useGetEmailContacts = () => {
  const [getContacts, { loading }] = useLazyQuery(LIST_EMAIL_CONTACTS_QUERY, {
    fetchPolicy: "network-only",
  });

  const fetchContacts = async (
    pageToken,
    search,
    emailAddressId,
    scope = "oem",
  ) => {
    const result = await getContacts({
      variables: { scope, pageToken, search, emailAddressId },
    });
    return result;
  };

  return { getContacts: fetchContacts, loading };
};

export const useGetEmailThreadsByFolder = () => {
  const [getThreads, { loading }] = useLazyQuery(
    LIST_EMAIL_THREADS_BY_FOLDER_QUERY,
    {
      fetchPolicy: "network-only",
    },
  );

  const fetchThreads = async (
    folderId,
    emailAddressId,
    pageToken = "",
    search = "",
    scope = "oem",
  ) => {
    const result = await getThreads({
      variables: { scope, folderId, emailAddressId, pageToken, search },
    });
    return result;
  };

  return { getThreads: fetchThreads, loading };
};

export const useGetEmailThread = (
  conversationId,
  emailAddressId,
  skip = false,
  scope = "oem",
  pollInterval = 0,
) => {
  const { data, loading, error, refetch } = useQuery(GET_EMAIL_THREAD_QUERY, {
    variables: { scope, conversationId, emailAddressId },
    fetchPolicy: "network-only",
    nextFetchPolicy: "network-only",
    skip: skip || !conversationId || !emailAddressId,
    pollInterval: skip || !conversationId || !emailAddressId ? 0 : pollInterval,
    notifyOnNetworkStatusChange: true,
  });

  return {
    thread: data?.getEmailThread ?? null,
    loading,
    error,
    refetch,
  };
};

export const useGetEmailMessage = (scope = "oem") => {
  const [getMessage, { loading }] = useLazyQuery(GET_EMAIL_MESSAGE_QUERY, {
    fetchPolicy: "network-only",
  });

  const fetchMessage = async (messageId, emailAddressId) => {
    const result = await getMessage({
      variables: { scope, messageId, emailAddressId },
    });
    return result.data?.getEmailMessage ?? null;
  };

  return { getMessage: fetchMessage, loading };
};

export const useSaveEmailDraft = (scope = "oem") => {
  const [save, { loading, error }] = useMutation(SAVE_EMAIL_DRAFT_MUTATION);

  const saveDraft = async (input) => {
    const result = await save({ variables: { scope, input } });
    return result.data?.saveEmailDraft ?? null;
  };

  return { saveDraft, loading, error };
};

export const useMarkEmailReadUnread = (scope = "oem") => {
  const [mark, { loading }] = useMutation(MARK_EMAIL_READ_UNREAD_MUTATION);

  const markReadUnread = async (conversationId, unread, emailAddressId) =>
    mark({ variables: { scope, conversationId, unread, emailAddressId } });

  return { markReadUnread, loading };
};

export const useDeleteEmailDraft = (scope = "oem") => {
  const [del, { loading }] = useMutation(DELETE_EMAIL_DRAFT_MUTATION);

  const deleteDraft = async (draftId, emailAddressId) =>
    del({ variables: { scope, draftId, emailAddressId } });

  return { deleteDraft, loading };
};

export const usePermanentlyDeleteEmails = (scope = "oem") => {
  const [del, { loading }] = useMutation(PERMANENTLY_DELETE_EMAILS_MUTATION);

  const permanentlyDeleteEmails = async (messageIds, emailAddressId) =>
    del({ variables: { scope, messageIds, emailAddressId } });

  return { permanentlyDeleteEmails, loading };
};

export const useUpdateEmailConversation = (scope = "oem") => {
  const [update, { loading }] = useMutation(UPDATE_EMAIL_CONVERSATION_MUTATION);

  const updateEmailThread = async (input) => {
    const result = await update({ variables: { scope, input } });
    return result.data?.updateEmailConversation ?? null;
  };

  return { updateEmailThread, loading };
};

export const useGetEmailMessageAttachments = (scope = "oem") => {
  const [getAttachments, { loading }] = useLazyQuery(
    GET_EMAIL_MESSAGE_ATTACHMENTS_QUERY,
    { fetchPolicy: "network-only" },
  );

  const fetchAttachments = async (messageId, emailAddressId) => {
    const result = await getAttachments({
      variables: { scope, messageId, emailAddressId },
    });
    return result.data?.getEmailMessageAttachments ?? [];
  };

  return { fetchAttachments, loading };
};

export const useGetEmailAttachmentContent = (scope = "oem") => {
  const ownerType = scope === "personal" ? "user" : "oem";

  const fetchAttachmentContent = async (
    messageId,
    attachmentId,
    emailAddressId,
  ) => {
    const encodedMessageId = encodeURIComponent(messageId);
    const encodedAttachmentId = encodeURIComponent(attachmentId);
    const encodedEmailAddressId = encodeURIComponent(emailAddressId);

    const response = await fetch(
      `/email/attachments/${encodedMessageId}/${encodedAttachmentId}?emailAddressId=${encodedEmailAddressId}&ownerType=${ownerType}`,
      {
        method: "GET",
        headers: {
          [AUTH.STRATEGIES.CLIENT.AUTH_HEADER]: CLIENT_AUTH_REQUEST_TYPE,
          [JWT.HEADER.TOKEN.NAME]:
            localStorage.getItem(JWT.LOCAL_STORAGE.TOKEN.NAME) || null,
          [JWT.HEADER.REFRESH_TOKEN.NAME]:
            localStorage.getItem(JWT.LOCAL_STORAGE.REFRESH_TOKEN.NAME) || null,
        },
      },
    );
    if (!response.ok)
      throw new Error(`Failed to fetch attachment: ${response.statusText}`);

    const contentType = response.headers.get("content-type") || "";

    if (contentType.includes("application/json")) {
      return response.json();
    }

    const blob = await response.blob();
    return {
      contentBytes: blob,
      contentType:
        response.headers.get("content-type") || "application/octet-stream",
      isBlob: true,
    };
  };

  return { fetchAttachmentContent, loading: false };
};

export const useListEmailThreadsByTicket = (scope = "oem") => {
  const [listThreads, { loading, refetch }] = useLazyQuery(
    LIST_EMAIL_THREADS_BY_TICKET_QUERY,
    { fetchPolicy: "network-only" },
  );

  const fetchThreads = async (ticketId, emailAddressId) => {
    const result = await listThreads({
      variables: { scope, ticketId, emailAddressId },
    });
    return result.data?.listEmailThreadsByTicket ?? [];
  };

  return { fetchThreads, loading, refetch };
};

export const useListEmailThreadsBySupportTicket = (scope = "oem") => {
  const [listThreads, { loading, refetch }] = useLazyQuery(
    LIST_EMAIL_THREADS_BY_SUPPORT_TICKET_QUERY,
    { fetchPolicy: "network-only" },
  );

  const fetchThreads = async (supportTicketId, emailAddressId) => {
    const result = await listThreads({
      variables: { scope, supportTicketId, emailAddressId },
    });
    return result.data?.listEmailThreadsBySupportTicket ?? [];
  };

  return { fetchThreads, loading, refetch };
};

export const useListEmailThreadsByCompany = () => {
  const [listThreads, { loading }] = useLazyQuery(
    LIST_EMAIL_THREADS_BY_COMPANY_QUERY,
    { fetchPolicy: "network-only" },
  );

  // Returns one page; pass the returned pageToken back for the next one.
  const fetchThreadsPage = async ({
    companyId,
    emailAddressId = null,
    pageToken = null,
  }) => {
    const result = await listThreads({
      variables: { companyId, emailAddressId, pageToken },
    });
    if (result.error) throw result.error;
    const page = result.data?.listEmailThreadsByCompany;
    return {
      threads: page?.threads ?? [],
      pageToken: page?.pageToken ?? null,
    };
  };

  return { fetchThreadsPage, loading };
};

export const useListCompanyEmailAccounts = (companyId) => {
  const { data, loading, refetch } = useQuery(
    LIST_COMPANY_EMAIL_ACCOUNTS_QUERY,
    {
      variables: { companyId },
      skip: !companyId,
      fetchPolicy: "network-only",
    },
  );
  return {
    accounts: data?.listCompanyEmailAccounts ?? [],
    loading,
    refetch,
  };
};

export const useGetCompanyEmailThread = (
  companyId,
  conversationId,
  emailAddressId,
  skip = false,
  pollInterval = 0,
) => {
  const disabled = skip || !companyId || !conversationId || !emailAddressId;
  const { data, loading, error, refetch } = useQuery(
    GET_COMPANY_EMAIL_THREAD_QUERY,
    {
      variables: { companyId, conversationId, emailAddressId },
      fetchPolicy: "network-only",
      nextFetchPolicy: "network-only",
      skip: disabled,
      pollInterval: disabled ? 0 : pollInterval,
      notifyOnNetworkStatusChange: true,
    },
  );
  return {
    thread: data?.getCompanyEmailThread ?? null,
    loading,
    error,
    refetch,
  };
};

export const uploadEmailFile = async (url, file) => {
  const formData = new FormData();
  formData.append("file", file, file.name);

  const response = await fetch(url, {
    method: "POST",
    body: formData,
  });

  if (!response.ok) {
    return null;
  }

  const data = await response.json();

  return data;
};

export const useGetEmailLink = (scope = "oem") => {
  const [getUrl, { data: { getEmailAuthUrl = "" } = {}, loading, error }] =
    useLazyQuery(GET_EMAIL_AUTH_LINK_QUERY);
  return {
    loading,
    error,
    url: getEmailAuthUrl,
    getAuthUrl: async () => getUrl({ variables: { scope } }),
  };
};
